import {
  getDatabaseContainerType,
  analyticsClient,
  SendDatabaseDeleteEntryEventSources,
} from '@databases/analytics';
import { copyToClipboard } from '@databases/copy-paste/copy-helper';
import {
  InlineRovoPromptTriggerSource,
  openInlineRovoPrompt,
} from '@databases/database-ai-first-creation-state/inline-rovo-prompt-state';
import { getBaseUrl } from '@databases/database-state';
import { type ShowPanelType } from '@databases/panel/PanelContext';
import { type DefaultRootState, type defaultStoreUtils } from '@databases/redux';
import { setHoverDeleteButton } from '@databases/redux/cellSelections/actions';
import { isEntrySelected } from '@databases/redux/cellSelections/selectors';
import {
  doClearEntireEntries,
  doDeleteEntries,
  doDuplicateEntireEntries,
  doSelectNewEntries,
  doMoveEntries,
  doInsertEntries,
} from '@databases/redux/contextual-menu/actions';
import { type DatabaseView } from '@databases/types/database/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import {
  getDatabasePath,
  getDatabaseLinkUrlWithQueryParams,
} from '@databases/utils/urls';

export type PositionType = 'before' | 'after';

type UseSelectEntriesActionsProps = {
  contentId: string;
  canEditContent: boolean;
  dispatch: ReturnType<typeof defaultStoreUtils.useDispatch>;
  entryId: string;
  view: DatabaseView;
  hidePanel: () => void;
  showPanel: ShowPanelType;
  panelEntryId: string;
  state: DefaultRootState;
};

export type UseSelectEntriesActionsReturnTypes = {
  handleAskRovo: () => void;
  handleClearEntries: () => void;
  handleCopyEntryLink: (setIsCopyLinkCopied: (isCopied: boolean) => void) => void;
  handleDeleteEntries: () => void;
  handleDuplicateEntries: () => void;
  handleExpandEntry: () => void;
  handleInsertEntries: (position: PositionType) => void;
  handleMoveEntries: (position: PositionType) => void;
};
export const buildSelectEntriesActions = ({
  contentId,
  canEditContent,
  dispatch,
  entryId,
  view,
  hidePanel,
  showPanel,
  panelEntryId,
  state,
}: UseSelectEntriesActionsProps): UseSelectEntriesActionsReturnTypes => {
  const database = selectDatabaseByContentIdRequired(state, {
    contentId,
  });
  const baseUrl = getBaseUrl();
  const databasePath = getDatabasePath({
    contentId,
    spaceKey: database.space.key,
  });

  const handleCopyEntryLink = (setIsCopyLinkCopied: (isCopied: boolean) => void) => {
    if (!baseUrl || !databasePath) {
      return;
    }

    const entryLinkCopyUrl = getDatabaseLinkUrlWithQueryParams({
      baseUrl: baseUrl,
      databasePath: databasePath,
      contentId,
      entryId,
      viewId: view.id,
    });

    copyToClipboard(entryLinkCopyUrl.toString(), 'text/plain');
    setIsCopyLinkCopied(true);

    setTimeout(() => setIsCopyLinkCopied(false), 3000);
  };

  const handleExpandEntry = () => {
    showPanel('EntryViewPanel', {
      entryId,
      isReadonly: !canEditContent,
    });
  };

  const handleDuplicateEntries = () => {
    dispatch(
      doSelectNewEntries(contentId, () => dispatch(doDuplicateEntireEntries(contentId)))
    );
  };

  const handleInsertEntries = async (position: PositionType) => {
    await analyticsClient.sendInsertRowEvent({
      contentId: contentId,
      databaseContainer: getDatabaseContainerType(),
    });

    await dispatch(
      doSelectNewEntries(contentId, () =>
        dispatch(doInsertEntries(contentId, view.settings, position === 'before'))
      )
    );
  };

  const handleMoveEntries = (position: PositionType) => {
    dispatch(doMoveEntries(contentId, position === 'before'));
  };

  const handleClearEntries = () => {
    dispatch(doClearEntireEntries(contentId));
  };

  const handleDeleteEntries = async () => {
    await analyticsClient.sendDeleteEntryEvent({
      contentId: contentId,
      databaseContainer: getDatabaseContainerType(),
      source: SendDatabaseDeleteEntryEventSources.ACTION_BAR,
    });
    if (isEntrySelected(state, contentId, panelEntryId)) {
      hidePanel();
    }

    dispatch(doDeleteEntries(contentId));
    dispatch(setHoverDeleteButton({ value: null }));
  };

  const handleAskRovo = () =>
    openInlineRovoPrompt(InlineRovoPromptTriggerSource.TableEntry);

  return {
    handleAskRovo,
    handleClearEntries,
    handleCopyEntryLink,
    handleDeleteEntries,
    handleDuplicateEntries,
    handleExpandEntry,
    handleInsertEntries,
    handleMoveEntries,
  };
};
